import { type JSX, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { submitApiRequestWithNewFiles } from 'Components/Files/FileManagementArea';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { FileToBeUploaded } from 'Models/Files';
import { DueDiligenceModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';
import { AssessmentFileReuseSelector, useAssessmentFileReuseSelection } from 'Pages/DueDiligenceAssessment/Components/AssessmentFileReuseSelector/AssessmentFileReuseSelector';

/** The single-area identifier passed to `submitApiRequestWithNewFiles`, which is otherwise built to spread new files across many controls/questions. */
const ASSESSMENT_WIDE_AREA = 'assessment-wide';

export interface ManageAssessmentWideFilesModalProps {
    assessment: TprmOrUcrmDueDiligenceAssessment;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    documentApi: DocumentApi;
    hideModal: () => void;
    module: DueDiligenceModule;
    onSuccess: (updatedAssessment: TprmOrUcrmDueDiligenceAssessment) => void;
}

/** Manages the files that SummIT Guide™ will consider against all controls in an assessment. Like the per-control files modal, it lets the user reuse files already uploaded elsewhere in the assessment or drop in new ones. */
export const ManageAssessmentWideFilesModal = (props: ManageAssessmentWideFilesModalProps): JSX.Element => {
    const selection = useAssessmentFileReuseSelection({
        assessment: props.assessment,
        initialSelectedUploadedFiles: props.assessment.assessment_wide_files,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alertState, setAlertState] = useState<{ type: 'success' | 'error'; message: string }>();

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setAlertState(undefined);

            const submitApiRequest = async (getFilesToBeUploaded: (identifier: string) => FileToBeUploaded[]): Promise<TprmOrUcrmDueDiligenceAssessment> => {
                const request = {
                    new_files: getFilesToBeUploaded(ASSESSMENT_WIDE_AREA),
                    selected_existing_file_ids: selection.selectedUploadedFiles.map((file) => file.file_id),
                };
                return (await props.assessmentApi.updateAssessmentWideFiles(props.assessment.third_party_id, props.assessment.service_id, props.assessment.id, request)).data;
            };

            const updatedAssessment = await submitApiRequestWithNewFiles(props.documentApi, new Map([[ASSESSMENT_WIDE_AREA, selection.selectedPendingFiles]]), submitApiRequest);

            props.onSuccess(updatedAssessment);
            setAlertState({ type: 'success', message: 'Files saved.' });
        } catch (error) {
            setAlertState({ type: 'error', message: error.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open header="Manage Assessment-Wide Files" subHeader="Manage the files that should be considered against all controls in the assessment. Files already added elsewhere in the assessment are available for reuse.">
            {alertState && <Alert type={alertState.type}>{alertState.message}</Alert>}
            <AssessmentFileReuseSelector disabled={alertState?.type === 'success'} internalFileCheckboxes={selection.internalFileCheckboxes} module={props.module} questionFileCheckboxes={selection.questionFileCheckboxes} onDropFiles={selection.onDropFiles} />
            <div className="modalFormButtonContainer">
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                <Button variant="primary" onClick={handleSubmit} fontAwesomeImage={ICON_SAVE} isLoading={isSubmitting} loadingText="Saving..." disabled={alertState?.type === 'success'}>
                    Save
                </Button>
            </div>
        </Modal>
    );
};
